import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight, Info, KeyRound, Lock, MessageCircleMore, Plus, Search, Sparkles } from "lucide-react";
import { isEmpty } from "lodash-es";

import { AppStateContext } from "@/state/AppStateContext";
import { getAllCustomGPTs } from "@/api/api";
import { CustomGPT } from "@/api/models";
import { categories } from "@/constants/categories";
import { doTrackEvent, resetChat, setTitle, toggleHistoryPanel, useHandleTransitionEnd } from "@/utils/utils";
import CustomGPTDetails from "@/components/CustomGPT/CustomGPTDetails";
import { Switch } from "@/components/ui/switch";

const OTHER_CATEGORY_ID = 999;
const RECENTLY_USED_LIMIT = 4;
const CATEGORY_PREVIEW_LIMIT = 5;

type BrowseView = { type: 'all' } | { type: 'category', id: number, label: string };

type CategoryGroup = {
  id: number;
  name: string;
  description: string;
  customGPTs: CustomGPT[];
};

/**
 * Strip the trailing emoji from a category name (e.g. 'Engineering & Technology 🔧')
 *
 * @param {string} name - The category name
**/
const categoryLabel = (name: string) => name.replace(/[^\p{Letter}\p{Number}\p{Punctuation}]+$/u, '').trim();

/**
 * Capitalize the first letter of the first two words of a Custom GPT name, used when it has no icon image
 *
 * @param {string} inputString - The Custom GPT's name
**/
const lettersAsIcon = (inputString: string) => {
  const words = inputString.split(' ');
  const initials = [];

  if (words.length >= 1) initials.push(words[0].charAt(0).toUpperCase());
  if (words.length >= 2) initials.push(words[1].charAt(0).toUpperCase());

  return initials.join('');
}

/**
 * Truncate a Custom GPT description to a maximum length
 *
 * @param {string} description - The Custom GPT's description
 * @param {number} max - The maximum number of characters
**/
const truncate = (description: string, max: number) => (description.length > max ? description.substring(0, max) + '...' : description);

/**
 * Compact conversation counts (999, 1k, 1.5k, 11k)
**/
const formatCount = (count: number) => (count < 1000 ? `${count}` : `${(count / 1000).toFixed(count < 10000 ? 1 : 0).replace(/\.0$/, '')}k`);

/**
 * Sort Custom GPTs alphabetically by name
**/
const byName = (a: CustomGPT, b: CustomGPT) => a.name.localeCompare(b.name);

/**
 * Sort Custom GPTs by conversation count, most used first
**/
const byUsage = (a: CustomGPT, b: CustomGPT) => (b.conversation_count || 0) - (a.conversation_count || 0);

interface AvatarProps {
  customGPT: CustomGPT;
  size: 'sm' | 'md' | 'lg';
}

const Avatar = ({ customGPT, size }: AvatarProps) => {
  const dimensions = size === 'lg' ? 'h-20 w-20' : (size === 'md' ? 'h-12 w-12' : 'h-9 w-9');
  const textSize = size === 'lg' ? 'text-3xl' : (size === 'md' ? 'text-lg' : 'text-xs');

  if (!isEmpty(customGPT.image_path)) {
    return <img src={`https://asappsmsstore.blob.core.windows.net/customgpticons/${customGPT.image_path}?v=${customGPT.updated_at || Date.now()}`} className={`${dimensions} shrink-0 rounded-full object-cover`} alt={customGPT.name} />;
  }

  return (
    <div className="avatar static placeholder shrink-0">
      <div className={`bg-neutral ${dimensions} rounded-full`}>
        <span className={textSize}>{lettersAsIcon(customGPT.name)}</span>
      </div>
    </div>
  );
}

/**
 * Visibility indicator shown next to a Custom GPT's name
**/
const VisibilityIcon = ({ customGPT }: { customGPT: CustomGPT }) => {
  if (customGPT.visibility == 2) return <Lock className="min-w-[16px] shrink-0" size={16} />;
  if (customGPT.visibility == 1) return <KeyRound className="min-w-[16px] shrink-0" size={16} />;
  return null;
}

interface CardProps {
  customGPT: CustomGPT;
  onOpen: (customGPT: CustomGPT) => void;
}

/**
 * Compact card used in the 'Recently used' row
**/
const RecentCard = ({ customGPT, onOpen }: CardProps) => (
  <button type="button" className="flex items-center bg-base-100 hover:bg-base-200 border border-black/5 dark:border-white/5 rounded-2xl text-start shadow-sm cursor-pointer gap-3 p-3" onClick={() => onOpen(customGPT)}>
    <Avatar customGPT={customGPT} size="md" />
    <span className="dark:text-white text-sm font-semibold truncate min-w-0">{customGPT.name}</span>
  </button>
);

/**
 * Large card used in the 'Featured' section and the list view; the footer shows usage
 * when featured and the creator(s) when listed
**/
const FeaturedCard = ({ customGPT, onOpen, meta }: CardProps & { meta: 'usage' | 'creators' }) => (
  <div className="flex items-stretch bg-base-100 hover:bg-base-200 border border-black/5 dark:border-white/5 rounded-3xl h-full shadow cursor-pointer gap-4 p-5" onClick={() => onOpen(customGPT)}>
    <Avatar customGPT={customGPT} size="lg" />
    <div className="flex flex-col flex-grow items-start text-start min-h-20 min-w-0">
      <div className="flex items-center w-full gap-1 mb-1">
        <h2 className="dark:text-white text-base font-bold truncate">{customGPT.name}</h2>
        <VisibilityIcon customGPT={customGPT} />
      </div>
      <p className="text-xs mb-3">{truncate(customGPT.description, 160)}</p>
      <span className="flex items-center text-xs text-black/60 dark:text-white/60 border-t border-black/10 dark:border-white/10 w-full mt-auto pt-2 gap-1">
        {meta === 'usage' ?
          <><MessageCircleMore size={14} />{formatCount(customGPT.conversation_count || 0)} conversation{customGPT.conversation_count === 1 ? `` : `s`}</>
        : <span className="truncate">By: <strong>{customGPT.created_by_name}</strong>{!isEmpty(customGPT.co_creator_name) ? <> &amp; <strong>{customGPT.co_creator_name}</strong></> : ``}</span>}
      </span>
    </div>
  </div>
);

/**
 * Single-line row used in the category sections
**/
const GPTRow = ({ customGPT, onOpen }: CardProps) => (
  <div className="flex items-center bg-base-100 hover:bg-base-200 border-b border-black/5 dark:border-white/5 last:border-b-0 cursor-pointer gap-4 px-4 py-3" onClick={() => onOpen(customGPT)}>
    <Avatar customGPT={customGPT} size="sm" />
    <div className="flex items-center gap-1 w-full lg:w-[260px] lg:shrink-0 min-w-0">
      <span className="dark:text-white text-sm font-semibold truncate">{customGPT.name}</span>
      <VisibilityIcon customGPT={customGPT} />
    </div>
    <div className="hidden lg:block flex-grow text-start text-xs truncate min-w-0">{truncate(customGPT.description, 120)}</div>
    <div className="hidden xl:block text-xs text-black/60 dark:text-white/60 truncate w-[220px] shrink-0">By: {customGPT.created_by_name}{!isEmpty(customGPT.co_creator_name) ? ` & ${customGPT.co_creator_name}` : ``}</div>
    <ChevronRight size={18} className="shrink-0 opacity-60" />
  </div>
);

const Store = () => {
  const appStateContext = useContext(AppStateContext);
  const ENV = appStateContext?.state?.frontendSettings?.display_env || '';
  const CREATOR_GROUP = appStateContext?.state.frontendSettings?.creator_group || '';
  const navigate = useNavigate();
  const { gptId } = useParams();
  useHandleTransitionEnd();
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const handleToggleHistoryPanel = () => toggleHistoryPanel(appStateContext);
  const [viewing, setViewing] = useState<boolean>(false);
  const [hasRun, setHasRun] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>('');
  const [creator, setCreator] = useState<number>(0);
  const [allCustomGPTs, setAllCustomGPTs] = useState<CustomGPT[]>([]);
  const [browse, setBrowse] = useState<BrowseView | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [selectedCustomGPT, setSelectedCustomGPT] = useState<CustomGPT | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isCreator = appStateContext?.state.userGroups.includes(CREATOR_GROUP) || ENV === 'DEV';
  const isSearching = !isEmpty(searchText.trim());
  const inListView = isSearching || browse !== null;

  /**
   * Handle checking for URL param to open a specific Custom GPT for editing
  **/
  useEffect(() => {
    if (!isEmpty(gptId)) setViewing(true);
  }, [gptId, appStateContext?.state.customGPTs]);

  /**
   * Track initial load of page
  **/
  useEffect(() => {
    setTitle(appStateContext, 'Spaces');
    resetChat(appStateContext);
    setHasRun(true);
  }, []);

  /**
   * Handle reloading Custom GPTs when toggle changes
  **/
  useEffect(() => {
    loadCustomGPTs();
  }, [viewing, creator]);

  /**
   * Scroll back to the top when switching between the store front page and the list view
  **/
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [browse, isSearching]);

  /**
   * Debounce search input (delay rate at which event fires and performs API call to query Custom GPTs)
  **/
  useEffect(() => {
    if (hasRun) {
      const h = setTimeout(() => { loadCustomGPTs(); }, 300);
      return () => clearTimeout(h);
    }
  }, [searchText]);

  /**
   * Handle loading all Custom GPTs
  **/
  const loadCustomGPTs = async () => {
    // Toggle loading state
    setLoading(true);

    // Get data
    const res = await getAllCustomGPTs(searchText, creator);
    setAllCustomGPTs(res);

    setLoading(false);

    // If a gptId is still present in the URL, check if it exists in the loaded Custom GPTs and open view dialog
    if (viewing) {
      const found = res.find(customGPT => customGPT.id === gptId);
      if (found) {
        navigate('/spaces');
        setSelectedCustomGPT(found);
        setIsViewDialogOpen(true);
      }
    }
  }

  /**
   * Group the loaded Custom GPTs into their categories, falling back to an 'Other' group; pinned
   * Custom GPTs belong to the 'Featured' section only, so they are left out of every category
  **/
  const categoryGroups: CategoryGroup[] = useMemo(() => {
    const groups: CategoryGroup[] = categories.map(category => ({ ...category, customGPTs: [] as CustomGPT[] }));
    const other: CategoryGroup = { id: OTHER_CATEGORY_ID, name: 'Other', description: 'Uncategorized Spaces', customGPTs: [] };

    allCustomGPTs.filter(customGPT => !customGPT.pinned).forEach(customGPT => {
      const group = groups.find(g => g.id == customGPT.category);
      (group ? group.customGPTs : other.customGPTs).push(customGPT);
    });

    return other.customGPTs.length > 0 ? [...groups, other] : groups;
  }, [allCustomGPTs]);

  /**
   * The pinned Custom GPTs, shown in the 'Featured' section
  **/
  const pinnedCustomGPTs = useMemo(() => allCustomGPTs.filter(customGPT => customGPT.pinned), [allCustomGPTs]);

  /**
   * The user's most recently used Custom GPTs, sourced from the same list that populates the sidebar (ordered by last used)
  **/
  const recentlyUsed = useMemo(() => {
    return (appStateContext?.state.customGPTs || [])
      .slice(0, RECENTLY_USED_LIMIT)
      .map(customGPT => allCustomGPTs.find(c => c.id === customGPT.id) || customGPT);
  }, [appStateContext?.state.customGPTs, allCustomGPTs]);

  /**
   * The Custom GPTs to render in the list view (search results, a single category, or everything), alphabetically
  **/
  const listViewCustomGPTs = useMemo(() => {
    if (browse?.type === 'category') return categoryGroups.find(g => g.id === browse.id)?.customGPTs.slice().sort(byName) || [];
    return allCustomGPTs.slice().sort(byName);
  }, [browse, categoryGroups, allCustomGPTs]);

  /**
   * Handle opening a specific Custom GPT
   *
   * @param {CustomGPT} customGPT - The Custom GPT to open
  **/
  const openCustomGPT = async (customGPT: CustomGPT) => {
    setSelectedCustomGPT(null);
    setTimeout(() => {
      setSelectedCustomGPT(customGPT);
      setIsViewDialogOpen(true);
    }, 100);
    handleTrackEvent('click', 'custom_gpt_view', `Custom GPT - View - ${customGPT.name || ''}`, '', customGPT.id || '', '');
  }

  /**
   * Handle switching into the list view
   *
   * @param {BrowseView} view - The list view to switch to
  **/
  const openBrowse = (view: BrowseView) => {
    // Browsing everything overrides the 'Mine' filter, which the loaded Custom GPTs are scoped by
    if (view.type === 'all') setCreator(0);
    setBrowse(view);
    handleTrackEvent('click', 'custom_gpt_browse', `Custom GPT - Browse - ${view.type === 'category' ? view.label : 'All'}`, '', '', '');
  }

  /**
   * Handle returning from the list view back to the store front page
  **/
  const closeBrowse = () => {
    setBrowse(null);
    setSearchText('');
  }

  return (
    <div className={`content flex flex-col h-full ${appStateContext?.state.sidepanelOpen ? 'shifted-left' : ''}`}>
      {!appStateContext?.state.sidepanelOpen && !appStateContext?.state.sidepanelTransitioning &&
        <div className="flex justify-between items-center mx-2">
          <button type="button" className="btn btn-ghost px-2" onClick={handleToggleHistoryPanel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-xl-heavy"><path fillRule="evenodd" clipRule="evenodd" d="M8.85719 3H15.1428C16.2266 2.99999 17.1007 2.99998 17.8086 3.05782C18.5375 3.11737 19.1777 3.24318 19.77 3.54497C20.7108 4.02433 21.4757 4.78924 21.955 5.73005C22.2568 6.32234 22.3826 6.96253 22.4422 7.69138C22.5 8.39925 22.5 9.27339 22.5 10.3572V13.6428C22.5 14.7266 22.5 15.6008 22.4422 16.3086C22.3826 17.0375 22.2568 17.6777 21.955 18.27C21.4757 19.2108 20.7108 19.9757 19.77 20.455C19.1777 20.7568 18.5375 20.8826 17.8086 20.9422C17.1008 21 16.2266 21 15.1428 21H8.85717C7.77339 21 6.89925 21 6.19138 20.9422C5.46253 20.8826 4.82234 20.7568 4.23005 20.455C3.28924 19.9757 2.52433 19.2108 2.04497 18.27C1.74318 17.6777 1.61737 17.0375 1.55782 16.3086C1.49998 15.6007 1.49999 14.7266 1.5 13.6428V10.3572C1.49999 9.27341 1.49998 8.39926 1.55782 7.69138C1.61737 6.96253 1.74318 6.32234 2.04497 5.73005C2.52433 4.78924 3.28924 4.02433 4.23005 3.54497C4.82234 3.24318 5.46253 3.11737 6.19138 3.05782C6.89926 2.99998 7.77341 2.99999 8.85719 3ZM6.35424 5.05118C5.74907 5.10062 5.40138 5.19279 5.13803 5.32698C4.57354 5.6146 4.1146 6.07354 3.82698 6.63803C3.69279 6.90138 3.60062 7.24907 3.55118 7.85424C3.50078 8.47108 3.5 9.26339 3.5 10.4V13.6C3.5 14.7366 3.50078 15.5289 3.55118 16.1458C3.60062 16.7509 3.69279 17.0986 3.82698 17.362C4.1146 17.9265 4.57354 18.3854 5.13803 18.673C5.40138 18.8072 5.74907 18.8994 6.35424 18.9488C6.97108 18.9992 7.76339 19 8.9 19H9.5V5H8.9C7.76339 5 6.97108 5.00078 6.35424 5.05118ZM11.5 5V19H15.1C16.2366 19 17.0289 18.9992 17.6458 18.9488C18.2509 18.8994 18.5986 18.8072 18.862 18.673C19.4265 18.3854 19.8854 17.9265 20.173 17.362C20.3072 17.0986 20.3994 16.7509 20.4488 16.1458C20.4992 15.5289 20.5 14.7366 20.5 13.6V10.4C20.5 9.26339 20.4992 8.47108 20.4488 7.85424C20.3994 7.24907 20.3072 6.90138 20.173 6.63803C19.8854 6.07354 19.4265 5.6146 18.862 5.32698C18.5986 5.19279 18.2509 5.10062 17.6458 5.05118C17.0289 5.00078 16.2366 5 15.1 5H11.5ZM5 8.5C5 7.94772 5.44772 7.5 6 7.5H7C7.55229 7.5 8 7.94772 8 8.5C8 9.05229 7.55229 9.5 7 9.5H6C5.44772 9.5 5 9.05229 5 8.5ZM5 12C5 11.4477 5.44772 11 6 11H7C7.55229 11 8 11.4477 8 12C8 12.5523 7.55229 13 7 13H6C5.44772 13 5 12.5523 5 12Z" fill="currentColor"></path></svg>
          </button>

          {isCreator ?
            <button type="button" className="btn btn-sm btn-primary shrink-0 pe-4" onClick={() => navigate('/spaces/c')}><Plus className="mt-0.5" size={20} /> Create</button>
          : null}
        </div>
      }

      <div className="flex flex-grow justify-center h-[90vh]">
        <div className="w-full mx-auto">
          <div className="main-div flex flex-col flex-grow items-center justify-center !h-full transitioned">
            <div className="child-div flex flex-col w-full gg">
              <div ref={scrollRef} className="child-div-parent flex flex-col flex-grow w-full items-center scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent pb-2">
                <div className="flex flex-col w-full text-start px-6">
                  <div className={`flex items-start justify-between gap-4 ${((!appStateContext?.state.sidepanelOpen && !appStateContext?.state.sidepanelTransitioning) ? '' : 'mt-4')}`}>
                    <div className="flex gap-4">
                      <div>
                        <h1 className="dark:text-white text-3xl font-bold">Spaces</h1>
                        {!window.matchMedia('(max-width: 700px)').matches ?
                          <>
                            <h4 className="dark:text-gray-400 text-sm mt-4">Discover and create custom versions of ALLY that combine custom instructions with your own additional domain-specific knowledge.</h4>
                            <h4 className="dark:text-gray-400 text-sm mt-2">If you have an idea for a new Space, please reach out to your <a className="text-primary underline" href="https://smsgroupgmbh.sharepoint.com/sites/intranet-content/SitePages/AI-Accelerators-and-Key-Users.aspx?xsdata=MDV8MDJ8fDdlNDA0ZmJmZWI5ZDRlZmNjNWExMDhkZGMyZDIzOWVhfDA2OWRiOTE0YzU0ODQ4OGZiOWNiYjUzNDA1YTA1ZDQ3fDB8MHw2Mzg4ODA5MzAzMjcyNDI3ODl8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKRFFTSTZJbFJsWVcxelgwRlVVRk5sY25acFkyVmZVMUJQVEU5R0lpd2lWaUk2SWpBdU1DNHdNREF3SWl3aVVDSTZJbGRwYmpNeUlpd2lRVTRpT2lKUGRHaGxjaUlzSWxkVUlqb3hNWDA9fDF8TDJOb1lYUnpMekU1T2pCbE1qRmxNV1ZsTFRNeU9URXROR1E1WVMwNFptTXdMV1ppWVRaaU4yVmpOVGhrTWw4ek1XTmxOV1E1WXkxa1lXUTVMVFF3WmpZdE9EZ3hOeTAzWWpKaE1XRTFOekF5Wm1aQWRXNXhMbWRpYkM1emNHRmpaWE12YldWemMyRm5aWE12TVRjMU1qUTVOakl6TVRZNE5BPT18MTkyYjJmMDhkN2E2NGRjM2M1YTEwOGRkYzJkMjM5ZWF8OWE4OGZlNzFjOTc4NDk5MDgzYTg5OGQ3MzFlY2Q2ZmQ%3D&sdata=bUNyYnVqZFErQzlnUUxTYzdzNzVKQW00V2VXNlNUNlBCbEVxUG5tbXk4UT0%3D&ovuser=069db914-c548-488f-b9cb-b53405a05d47%2Cej.cox%40sms-group.com&OR=Teams-HL&CT=1752496272768&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiI0OS8yNTA2MTIxNjQyNCIsIkhhc0ZlZGVyYXRlZFVzZXIiOmZhbHNlfQ%3D%3D" target="_blank">AI Accelerator</a>.</h4>
                          </>
                        : null}
                      </div>
                    </div>

                    {(appStateContext?.state.sidepanelOpen || appStateContext?.state.sidepanelTransitioning) && isCreator &&
                      <button type="button" className="btn btn-sm btn-primary shrink-0 pe-4" onClick={() => navigate('/spaces/c')}><Plus className="mt-0.5" size={20} /> Create</button>
                    }
                  </div>

                  {/* Search */}
                  <div className="w-full mt-6">
                    <label className="input bg-neutral border-accent border-opacity-50 focus-within:border-accent focus-within:border-opacity-50 focus-within:outline-none flex items-center gap-2">
                      <Search size={16} className="opacity-70 z-0" />
                      <input type="text" className="grow" placeholder="Search Spaces..." autoComplete="off" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </label>
                  </div>

                  {/* Toolbar */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                    {isCreator ?
                      <div className="flex relative bg-secondary rounded-md p-1">
                        <Switch className="dark:text-white text-sm w-[75px] p-2" checked={creator == 0} onCheckedChange={() => setCreator(0)}>All</Switch>
                        <Switch className="dark:text-white text-sm w-[75px] p-2" checked={creator == 1} onCheckedChange={() => setCreator(1)}>Mine</Switch>
                      </div>
                    : <span />}

                    <button type="button" className="btn btn-sm btn-ghost text-primary" onClick={() => { appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: 'cgpt_selector' }); navigate('/'); handleTrackEvent('click', 'try_custom_gpt_selector', 'Try Custom GPT Selector', '', '', ''); }}>
                      Not sure what you need? Ask the selector
                      <ArrowRight size={16} />
                    </button>
                  </div>

                  {loading ?
                    <>
                      {/* Recently used */}
                      <div className="w-full mt-6">
                        <div className="skeleton h-6 w-40 mb-3"></div>
                        <div className="grid sm:grid-cols-2 xl:grid-cols-4 w-full gap-6">
                          {Array.from({ length: RECENTLY_USED_LIMIT }, (_, index) => index).map((x, idx) =>
                            <div key={idx} className="flex items-center bg-base-100 border border-black/5 dark:border-white/5 rounded-2xl gap-3 p-3">
                              <div className="skeleton h-12 w-12 shrink-0 rounded-full"></div>
                              <div className="skeleton h-4 w-2/3"></div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Featured */}
                      <div className="w-full mt-6">
                        <div className="skeleton h-6 w-32"></div>
                        <div className="skeleton h-3 w-56 mt-2"></div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-6 mt-3">
                          {Array.from({ length: 3 }, (_, index) => index).map((x, idx) =>
                            <div key={idx} className="flex items-stretch bg-base-100 border border-black/5 dark:border-white/5 rounded-3xl gap-4 p-5">
                              <div className="skeleton h-20 w-20 shrink-0 rounded-full"></div>
                              <div className="flex flex-col flex-grow min-h-20 min-w-0">
                                <div className="skeleton h-4 w-2/3 mb-2"></div>
                                <div className="skeleton h-3 w-full mb-1"></div>
                                <div className="skeleton h-3 w-4/5 mb-3"></div>
                                <div className="skeleton h-3 w-1/3 mt-auto"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Categories */}
                      {Array.from({ length: 2 }, (_, index) => index).map((x, c_idx) =>
                        <div key={c_idx} className="w-full mt-6">
                          <div className="skeleton h-6 w-48"></div>
                          <div className="skeleton h-3 w-64 mt-2"></div>
                          <div className="flex flex-col bg-base-100 border border-black/5 dark:border-white/5 rounded-2xl overflow-hidden shadow-sm mt-3">
                            {Array.from({ length: CATEGORY_PREVIEW_LIMIT }, (_, index) => index).map((y, idx) =>
                              <div key={idx} className="flex items-center border-b border-black/5 dark:border-white/5 last:border-b-0 gap-4 px-4 py-3">
                                <div className="skeleton h-9 w-9 shrink-0 rounded-full"></div>
                                <div className="skeleton h-4 w-full lg:w-[260px] lg:shrink-0"></div>
                                <div className="skeleton hidden lg:block h-3 flex-grow"></div>
                                <div className="skeleton hidden xl:block h-3 w-[220px] shrink-0"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  : inListView ?
                    /* List view - search results, a single category, or everything */
                    <div className="w-full mt-8">
                      <div className="flex items-center gap-3">
                        <button type="button" className="btn btn-sm btn-ghost border border-accent text-accent hover:border-accent hover:bg-accent dark:hover:text-white px-3" onClick={closeBrowse}><ArrowLeft size={16} /></button>
                        <h2 className="dark:text-white text-xl font-bold">
                          {isSearching ? `Results for "${searchText.trim()}"` : (browse?.type === 'category' ? categoryLabel(browse.label) : 'All Spaces')}
                        </h2>
                        <span className="text-xs text-black/60 dark:text-white/60 ms-auto shrink-0">{listViewCustomGPTs.length} {listViewCustomGPTs.length === 1 ? 'Space' : 'Spaces'}</span>
                      </div>

                      {listViewCustomGPTs.length === 0 ?
                        <div className="text-sm text-black/60 dark:text-white/60 mt-6">No Spaces found.</div>
                      :
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-6 mt-4">
                          {listViewCustomGPTs.map((customGPT, idx) => <FeaturedCard key={customGPT.id || idx} customGPT={customGPT} onOpen={openCustomGPT} meta="creators" />)}
                        </div>
                      }
                    </div>
                  :
                    <>
                      {/* Recently used */}
                      {creator == 0 && recentlyUsed.length > 0 &&
                        <div className="w-full mt-6">
                          <h2 className="dark:text-white text-xl font-bold mb-3">Recently used</h2>
                          <div className="grid sm:grid-cols-2 xl:grid-cols-4 w-full gap-6">
                            {recentlyUsed.map((customGPT, idx) => <RecentCard key={customGPT.id || idx} customGPT={customGPT} onOpen={openCustomGPT} />)}
                          </div>
                        </div>
                      }

                      {/* Featured */}
                      {creator == 0 && pinnedCustomGPTs.length > 0 &&
                        <div className="w-full mt-8">
                          <h2 className="dark:text-white text-xl font-bold">Featured</h2>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-6 mt-3">
                            {pinnedCustomGPTs.map((customGPT, idx) => <FeaturedCard key={customGPT.id || idx} customGPT={customGPT} onOpen={openCustomGPT} meta="usage" />)}
                          </div>
                        </div>
                      }

                      {/* Categories */}
                      {categoryGroups.filter(group => group.customGPTs.length > 0).map((group, c_idx) => {
                        const preview = group.customGPTs.slice().sort(byUsage).slice(0, CATEGORY_PREVIEW_LIMIT);

                        return (
                          <div key={c_idx} className="w-full mt-8">
                            <div className="flex items-end justify-between gap-4">
                              <div>
                                <h2 className="dark:text-white text-xl font-bold">{categoryLabel(group.name)}</h2>
                                <span className="text-xs">{group.description}</span>
                              </div>
                              <button type="button" className="btn btn-sm btn-ghost shrink-0 text-xs" onClick={() => openBrowse({ type: 'category', id: group.id, label: group.name })}>View all</button>
                            </div>
                            <div className="flex flex-col bg-base-100 border border-black/5 dark:border-white/5 rounded-2xl overflow-hidden shadow-sm mt-3">
                              {preview.map((customGPT, idx) => <GPTRow key={customGPT.id || idx} customGPT={customGPT} onOpen={openCustomGPT} />)}
                            </div>
                          </div>
                        );
                      })}

                      {/* Browse all */}
                      <div className="flex flex-wrap items-center justify-between bg-base-100 border border-black/5 dark:border-white/5 rounded-2xl gap-4 mt-6 p-5">
                        <div className="flex items-center gap-4">
                          <Sparkles size={22} className="shrink-0 opacity-70" />
                          <div>
                            <h3 className="dark:text-white text-sm font-bold">Explore more</h3>
                          </div>
                        </div>
                        <button type="button" className="btn btn-sm rounded-full" onClick={() => openBrowse({ type: 'all' })}>Browse all Spaces <ArrowRight size={16} /></button>
                      </div>
                    </>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomGPTDetails isViewDialogOpen={isViewDialogOpen} setIsViewDialogOpen={setIsViewDialogOpen} selectedCustomGPT={selectedCustomGPT} editable={true} />
    </div>
  );
}

export default Store;
